import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, rem, Text } from '@bonusly/design-system';

import { useSuppressBizyFlight } from '../../../../bizy';
import { shouldIgnoreDoodleShortcut } from '../../../../doodle-editor/keyboard-shortcuts';
import { DOODLE_CANVAS, DOODLE_EXPORT_SCALE, DoodleCanvas } from './doodle-canvas';
import { DoodleToolbar } from './doodle-toolbar';
import { canvasToBlob, hasRenderedContent, renderStrokeToCtx } from './stroke-utils';
import { useDoodle } from './use-doodle';

interface DoodleModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (file: File) => void | Promise<void>;
}

const waitForNextPaint = (): Promise<void> =>
	new Promise((resolve) => {
		requestAnimationFrame(() => resolve());
	});

export const DoodleModal: FC<DoodleModalProps> = ({ isOpen, onClose, onSave }) => {
	const { t } = useTranslation('action_input');
	useSuppressBizyFlight(isOpen);
	const {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		eraserSize,
		setEraserSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		undo,
		redo,
		clear,
		canUndo,
		canRedo,
		hasContent,
	} = useDoodle();
	const isSaving = useRef(false);
	const [isShowingSaveFeedback, setIsShowingSaveFeedback] = useState(false);
	const popoverPortalRef = useRef<HTMLDivElement>(null);
	const strokesRef = useRef(strokes);
	strokesRef.current = strokes;

	const handleSave = useCallback(async () => {
		if (isSaving.current || !hasRenderedContent(strokesRef.current)) return;
		isSaving.current = true;
		setIsShowingSaveFeedback(true);

		try {
			await waitForNextPaint();
			const exportCanvas = document.createElement('canvas');
			exportCanvas.width = DOODLE_CANVAS.width * DOODLE_EXPORT_SCALE;
			exportCanvas.height = DOODLE_CANVAS.height * DOODLE_EXPORT_SCALE;
			const ctx = exportCanvas.getContext('2d');
			if (!ctx) return;

			ctx.scale(DOODLE_EXPORT_SCALE, DOODLE_EXPORT_SCALE);
			ctx.fillStyle = '#ffffff';
			ctx.fillRect(0, 0, DOODLE_CANVAS.width, DOODLE_CANVAS.height);

			for (const stroke of strokesRef.current) {
				renderStrokeToCtx(ctx, stroke);
			}

			const blob = await canvasToBlob(exportCanvas);
			const file = new File([blob], `doodle-${Date.now()}.png`, { type: 'image/png' });
			await onSave(file);
			clear();
			onClose();
		} catch {
			// Keep the drawing open so a failed upload can be retried.
		} finally {
			isSaving.current = false;
			setIsShowingSaveFeedback(false);
		}
	}, [onSave, onClose, clear]);

	const handleClose = useCallback(() => {
		clear();
		onClose();
	}, [clear, onClose]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (shouldIgnoreDoodleShortcut(e.target)) return;

			const isMod = e.metaKey || e.ctrlKey;

			if (isMod && e.key === 'z' && !e.shiftKey) {
				e.preventDefault();
				undo();
				return;
			}
			if (isMod && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
				e.preventDefault();
				redo();
				return;
			}

			if (isMod || e.altKey) return;

			switch (e.key.toLowerCase()) {
				case 'b':
					setTool('brush');
					break;
				case 'e':
					setTool('eraser');
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, undo, redo, setTool]);

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="4xl" closeOnOverlayClick={false}>
			<ModalOverlay />
			<ModalContent
				ref={popoverPortalRef}
				maxW={{ base: 'calc(100vw - 24px)', md: rem(770) }}
				mx="auto"
				p={5}
				borderRadius={{ base: 'xl', md: rem(24) }}
				bg="bg.surface"
			>
				<ModalHeader p={0} pr={10} mb={4} color="text.default" borderBottom="none">
					<Text fontSize="lg" fontWeight="semibold">
						{t('doodle.title')}
					</Text>
				</ModalHeader>
				<ModalCloseButton top={5} right={5} size="md" borderRadius="full" bg="secondary.bgAlpha" />

				<ModalBody p={0}>
					<Box borderWidth={rem(1)} borderStyle="solid" borderColor="border.default" borderRadius={rem(16)} overflow="hidden">
						<DoodleCanvas strokes={strokes} currentStroke={currentStroke} onStartStroke={startStroke} onAddPoint={addPoint} onEndStroke={endStroke} />
						<DoodleToolbar
							tool={tool}
							color={color}
							size={size}
							eraserSize={eraserSize}
							isDrawing={currentStroke !== null}
							canUndo={canUndo}
							canRedo={canRedo}
							onToolChange={setTool}
							onColorChange={setColor}
							onSizeChange={setSize}
							onEraserSizeChange={setEraserSize}
							onUndo={undo}
							onRedo={redo}
							onCancel={handleClose}
							onSave={handleSave}
							isSaveDisabled={!hasContent || currentStroke !== null}
							isSaving={isShowingSaveFeedback}
							portalContainerRef={popoverPortalRef}
						/>
					</Box>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
